import { useState, useEffect, useRef, useCallback } from 'react';
import type { StoryElement } from '../types';
import { recordEvent } from '../repositories/storyRepository';
import { buildArticleClipboardText } from '../utils/clipboard';
import { publicStoryShortcutAction, shortcutKeyMatches } from '../utils/keyboard';
import { toggleStoryBookmark } from '../utils/toggleStoryBookmark';
import { isModalOpen } from '../components/ModalOverlay';
import { useIsAdminUser } from '../providers/UserProvider';
import type { UserEventContext } from '../repositories/storyRepository';

interface UseStoryKeyboardNavResult {
  visibleElements: StoryElement[];
  focusedIndex: number;
  expandedIds: Set<string>;
  focusStory: (id: string) => void;
  /** Move both list focus and DOM focus to the first visible row. */
  focusTopStory: () => void;
  toggleExpanded: (id: string) => void;
  setRowRef: (i: number) => (el: HTMLElement | null) => void;
  notifyStoryUpdate: () => void;
}

/**
 * j/k/o/l/Enter/y/d/a/arrow/f keyboard navigation over a list of stories.
 * Shared by the feed reader (StoryList) and the saved-search results pane
 * (SearchPane) so both behave identically.
 *
 * `resetKey` should change whenever the underlying feed/search changes; the
 * focused row and expanded rows reset when it does.
 */
export function useStoryKeyboardNav(
  elements: StoryElement[],
  resetKey: string,
  eventContext: UserEventContext,
): UseStoryKeyboardNavResult {
  // Admins spend 'a' on the admin-mode toggle (issue #889); 'o' and ArrowRight
  // still expand, so nothing is lost.
  const isAdminUser = useIsAdminUser();
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [, forceRender] = useState(0);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const rowRefs = useRef<(HTMLElement | null)[]>([]);
  const focusedArticleIdRef = useRef<string | null>(null);

  const notifyStoryUpdate = useCallback(() => {
    forceRender((n) => n + 1);
  }, []);

  // Reset expanded rows and focus when the feed/search changes.
  useEffect(() => {
    setFocusedIndex(0);
    focusedArticleIdRef.current = null;
    setExpandedIds(new Set());
  }, [resetKey]);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const focusStory = useCallback((id: string) => {
    const nextIndex = elements.findIndex((element) => element.id === id);
    if (nextIndex < 0) return;
    focusedArticleIdRef.current = id;
    setFocusedIndex(nextIndex);
  }, [elements]);

  /**
   * Hand the keyboard to the top row.
   *
   * Sets DOM focus, not just the list index: the shortcut handler ignores keys
   * typed with a textarea focused (`isTyping`), so a caller whose composer
   * holds focus — the search box after a run — would otherwise leave j/k
   * going into the query box.
   */
  const focusTopStory = useCallback(() => {
    const first = elements[0];
    if (!first) return;
    focusedArticleIdRef.current = first.id;
    setFocusedIndex(0);
    const row = rowRefs.current[0];
    row?.focus({ preventScroll: true });
    row?.scrollIntoView({ block: 'nearest' });
  }, [elements]);

  // Keep the focused row pointed at the same article as the list changes.
  useEffect(() => {
    const focusedArticleId = focusedArticleIdRef.current;
    if (!focusedArticleId) {
      setFocusedIndex((i) => Math.min(i, Math.max(elements.length - 1, 0)));
      return;
    }
    const nextIndex = elements.findIndex((element) => element.id === focusedArticleId);
    if (nextIndex >= 0) {
      setFocusedIndex(nextIndex);
      return;
    }
    setFocusedIndex((i) => {
      const next = Math.min(i, Math.max(elements.length - 1, 0));
      focusedArticleIdRef.current = elements[next]?.id ?? null;
      return next;
    });
  }, [elements]);

  // Scroll focused row into view when index changes
  useEffect(() => {
    rowRefs.current[focusedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex]);

  useEffect(() => {
    if (elements.length === 0) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
      if (isModalOpen()) return;
      const publicAction = publicStoryShortcutAction(e, isTyping);
      if (publicAction === 'focus_next') {
        setFocusedIndex((i) => {
          const next = Math.min(i + 1, elements.length - 1);
          focusedArticleIdRef.current = elements[next]?.id ?? null;
          return next;
        });
      } else if (publicAction === 'focus_previous') {
        setFocusedIndex((i) => {
          const next = Math.max(i - 1, 0);
          focusedArticleIdRef.current = elements[next]?.id ?? null;
          return next;
        });
      } else if (publicAction === 'open_source') {
        const story = elements[focusedIndex];
        if (story?.url) { window.open(story.url, '_blank', 'noreferrer'); recordEvent(story.id, 'click_external', eventContext); }
      } else if (publicAction === 'toggle_details') {
        e.preventDefault();
        const story = elements[focusedIndex];
        if (story) {
          if (!expandedIds.has(story.id)) void recordEvent(story.id, 'show_summary', eventContext);
          toggleExpanded(story.id);
        }
      } else if (isTyping || e.metaKey || e.ctrlKey || e.altKey) {
        return;
      } else if (shortcutKeyMatches(e, 'y')) {
        const story = elements[focusedIndex];
        if (story) {
          e.preventDefault();
          navigator.clipboard.writeText(buildArticleClipboardText(story));
        }
      } else if (!isAdminUser && shortcutKeyMatches(e, 'a')) {
        e.preventDefault();
        const story = elements[focusedIndex];
        if (!story) return;
        if (!expandedIds.has(story.id)) void recordEvent(story.id, 'show_summary', eventContext);
        toggleExpanded(story.id);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        const story = elements[focusedIndex];
        if (!story) return;
        if (!expandedIds.has(story.id)) void recordEvent(story.id, 'show_summary', eventContext);
        setExpandedIds((prev) => new Set(prev).add(story.id));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const story = elements[focusedIndex];
        if (!story) return;
        setExpandedIds((prev) => {
          const next = new Set(prev);
          next.delete(story.id);
          return next;
        });
      } else if (shortcutKeyMatches(e, 'f')) {
        const story = elements[focusedIndex];
        if (story) {
          e.preventDefault();
          void toggleStoryBookmark(story, notifyStoryUpdate);
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [elements, focusedIndex, toggleExpanded, notifyStoryUpdate, isAdminUser, expandedIds, eventContext]);

  const setRowRef = useCallback((i: number) => (el: HTMLElement | null) => {
    rowRefs.current[i] = el;
  }, []);

  return {
    visibleElements: elements,
    focusedIndex,
    expandedIds,
    focusStory,
    focusTopStory,
    toggleExpanded,
    setRowRef,
    notifyStoryUpdate,
  };
}
